import React from "react";
import PropTypes from "prop-types";
import { pure } from "recompose";
import { defineMessages, injectIntl, FormattedMessage } from "react-intl";
import styles from "./CustomerForm.scss";
import classnames from "classnames";
import Notification from "~/components/Notification/Notification";
import { renderHTML } from "../../utils/misc";
import phoneCountryPrefixes from "../../utils/phoneCountryPrefixes";
import {
  ExtrasAdditionalInfo,
  FormGenerator,
  CommunicationPolicy,
} from "@witbooking/react-components";
import styled from "styled-components";
import constants from "../../utils/constants";

const msgs = defineMessages({
  title: {
    id: "checkout.customer_form.title",
    description: "title",
    defaultMessage: "Guest Information",
  },
  name: {
    id: "checkout.customer_form.name",
    description: "name",
    defaultMessage: "Name *",
  },
  lastName: {
    id: "checkout.customer_form.lastname",
    description: "lastname",
    defaultMessage: "Lastname *",
  },
  phone: {
    id: "checkout.customer_form.phone",
    description: "phone",
    defaultMessage: "Phone *",
  },
  email: {
    id: "checkout.customer_form.email",
    description: "email",
    defaultMessage: "Email *",
  },
  repeatEmail: {
    id: "checkout.customer_form.repeat",
    description: "email repeat",
    defaultMessage: "Repeat Email *",
  },
  address: {
    id: "checkout.customer_form.address",
    description: "address",
    defaultMessage: "Address *",
  },
  idDocument: {
    id: "checkout.customer_form.idDocument",
    description: "idDocument",
    defaultMessage: "Id Document *",
  },
  comments: {
    id: "checkout.customer_form.comments",
    description: "comments",
    defaultMessage: "Comments",
  },
  country: {
    id: "checkout.customer_form.country",
    description: "country",
    defaultMessage: "Country",
  },
  arrivalTime: {
    id: "checkout.customer_form.arrivalTime",
    description: "arrivalTime",
    defaultMessage: "Arrival Time *",
  },
  promoField: {
    id: "checkout.customer_form.promoField",
    description: "promoField",
    defaultMessage: "Receive promotions",
  },
  readMore: {
    id: "checkout.customer_form.readMore",
    description: "Read more",
    defaultMessage: "Read more",
  },
});
const hours = [
  { value: "00:00", text: "12:00am" },
  { value: "00:30", text: "12:30am" },
  { value: "01:00", text: "1:00am" },
  { value: "01:30", text: "1:30am" },
  { value: "02:00", text: "2:00am" },
  { value: "02:30", text: "2:30am" },
  { value: "03:00", text: "3:00am" },
  { value: "03:30", text: "3:30am" },
  { value: "04:00", text: "4:00am" },
  { value: "04:30", text: "4:30am" },
  { value: "05:00", text: "5:00am" },
  { value: "05:30", text: "5:30am" },
  { value: "06:00", text: "6:00am" },
  { value: "06:30", text: "6:30am" },
  { value: "07:00", text: "7:00am" },
  { value: "07:30", text: "7:30am" },
  { value: "08:00", text: "8:00am" },
  { value: "08:30", text: "8:30am" },
  { value: "09:00", text: "9:00am" },
  { value: "09:30", text: "9:30am" },
  { value: "10:00", text: "10:00am" },
  { value: "10:30", text: "10:30am" },
  { value: "11:00", text: "11:00am" },
  { value: "11:30", text: "11:30am" },
  { value: "12:00", text: "12:00pm" },
  { value: "12:30", text: "12:30pm" },
  { value: "13:00", text: "1:00pm" },
  { value: "13:30", text: "1:30pm" },
  { value: "14:00", text: "2:00pm" },
  { value: "14:30", text: "2:30pm" },
  { value: "15:00", text: "3:00pm" },
  { value: "15:30", text: "3:30pm" },
  { value: "16:00", text: "4:00pm" },
  { value: "16:30", text: "4:30pm" },
  { value: "17:00", text: "5:00pm" },
  { value: "17:30", text: "5:30pm" },
  { value: "18:00", text: "6:00pm" },
  { value: "18:30", text: "6:30pm" },
  { value: "19:00", text: "7:00pm" },
  { value: "19:30", text: "7:30pm" },
  { value: "20:00", text: "8:00pm" },
  { value: "20:30", text: "8:30pm" },
  { value: "21:00", text: "9:00pm" },
  { value: "21:30", text: "9:30pm" },
  { value: "22:00", text: "10:00pm" },
  { value: "22:30", text: "10:30pm" },
  { value: "23:00", text: "11:00pm" },
  { value: "23:30", text: "11:30pm" },
];

const StyledFormGenerator = styled(FormGenerator)`
  & > * {
    margin: 7.5px 7.5px 0 7.5px;
  }

  & > *:last-child {
    margin-bottom: 7.5px;
  }
  label {
    margin-top: -5px;
    display: block;
  }
`;

function isFieldInvalid(fieldName, fields) {
  return fields[fieldName] && fields[fieldName].touched && fields[fieldName].error;
}

function isRepeatEmailInvalid(fields) {
  const emailValue = fields.email && fields.email.value;
  const repeatEmailValue = fields.repeatEmail && fields.repeatEmail.value;
  return emailValue !== repeatEmailValue;
}

function CustomerForm({
  intl: { formatMessage } = {},
  fields: { name, lastName, email, comments },
  fields,
  messages,
  countries,
  propertyTicker,
  fieldConfiguration,
  communicationsDisclaimer,
  handleModalContent,
  reservations,
  guestFormQuestionnaire,
  updateCustomFormValues,
  isFormValid,
  reservationsDates,
  touchFormGenerator,
  handleCloseModal,
}) {
  const renderFieldPlaceHolder = fieldName => {
    return (
      ((fieldConfiguration.get(fieldName).get("required") && "*") || "") +
      " " +
      formatMessage(msgs[fieldName])
    );
  };

  const handlePromosModal = () => {
    handleModalContent(
      <div className={classnames("wbkv9-CustomerForm-promoText", styles.promoText)}>
        <CommunicationPolicy
          data={<span dangerouslySetInnerHTML={{ __html: communicationsDisclaimer }} />}
          closeWindow={handleCloseModal}
        />
      </div>,
      {
        hideCloseButton: true,
      }
    );
  };

  const handleCountryChange = selectedCountry => {
    fields.country.onChange(selectedCountry);

    if (fields.phonePrefix && phoneCountryPrefixes) {
      phoneCountryPrefixes.some(country => {
        if (selectedCountry === country.code) {
          fields.phonePrefix.onChange(country.dial_code);
          return true;
        }
      });
    }
  };

  //This removes invalid from passed props. It's not ok to pass it down.
  const { invalid: _a, ...nameProps } = name || {};
  const { invalid: _b, ...lastNameProps } = lastName || {};
  const { invalid: _c, ...countryProps } = fields.country || {};
  const { invalid: _d, ...emailProps } = email || {};
  const { invalid: _e, ...repeatEmailProps } = fields.repeatEmail || {};
  const { invalid: _f, ...phoneProps } = fields.phone || {};
  const { invalid: _ff, ...phonePrefixProps } = fields.phonePrefix || {};
  const { invalid: _g, ...addressProps } = fields.address || {};
  const { invalid: _h, ...commentsProps } = comments || {};
  const { invalid: _i, ...newsletterProps } = fields.newsletter || {};
  const { invalid: _j, ...idDocumentProps } = fields.idDocument || {};
  const { invalid: _k, ...arrivalTimeProps } = fields.arrivalTime || {};

  if (!arrivalTimeProps.value) {
    arrivalTimeProps.value = "";
  }

  return (
    <div className={classnames("wbkv9-CustomerForm", styles.customerFormContainer)}>
      <div className={classnames("wbkv9-CustomerForm-title", styles.title)}>
        <FormattedMessage {...msgs.title} />
      </div>
      {messages
        ? messages.map((el, index) => (
            <Notification
              key={index}
              id={el.get("id")}
              position={el.get("position")}
              text={<span dangerouslySetInnerHTML={renderHTML(el.get("content"))} />}
              type={el.get("type").toLowerCase()}
              propertyTicker={propertyTicker}
              hideIcon
            />
          ))
        : false}
      <div className={classnames("wbkv9-CustomerForm-innerContainer", styles.innerContainer)}>
        <div className={classnames("wbkv9-CustomerForm-customerForm", styles.customerForm)}>
          <div className={classnames("wbkv9-CustomerForm-fieldGroup", styles.fieldGroup)}>
            <div
              className={classnames(
                "wbkv9-CustomerForm-field",
                styles.field,
                isFieldInvalid("name", fields) && styles.invalid
              )}>
              <input type="text" placeholder={renderFieldPlaceHolder("name")} {...nameProps} />
            </div>
            <div
              className={classnames(
                "wbkv9-CustomerForm-field",
                styles.field,
                isFieldInvalid("lastName", fields) && styles.invalid
              )}>
              <input
                type="text"
                placeholder={renderFieldPlaceHolder("lastName")}
                {...lastNameProps}
              />
            </div>

            {fields.country && (
              <div
                className={classnames(
                  "wbkv9-CustomerForm-field",
                  styles.field,
                  isFieldInvalid("country", fields) && styles.invalid
                )}>
                <select
                  type="text"
                  {...countryProps}
                  className={classnames({
                    "wbkv9-CustomerForm-field-country--selected": !!fields.country.value,
                    [styles.selected]: !!fields.country.value,
                    "wbkv9-CustomerForm-field-country--unselected": !fields.country.value,
                    [styles.unselected]: !fields.country.value,
                  })}
                  onChange={event => handleCountryChange(event.target.value)}>
                  <option value="" disabled hidden>
                    {"* " + formatMessage(msgs.country)}
                  </option>
                  {countries &&
                    countries.map((country, idx) => (
                      <option key={idx} value={country.get("code")}>
                        {country.get("name")}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {fields.phone && (
              <div
                className={classnames(
                  "wbkv9-CustomerForm-field",
                  "wbkv9-CustomerForm-phoneContainer",
                  styles.field,
                  styles.phoneContainer,
                  isFieldInvalid("phone", fields) && styles.invalid
                )}>
                <select {...phonePrefixProps}>
                  {phoneCountryPrefixes &&
                    phoneCountryPrefixes.map((country, idx) => (
                      <option key={idx} value={country.dial_code}>
                        {country.dial_code}
                      </option>
                    ))}
                </select>
                <input type="tel" placeholder={renderFieldPlaceHolder("phone")} {...phoneProps} />
              </div>
            )}
            <div
              className={classnames(
                "wbkv9-CustomerForm-field",
                styles.field,
                isFieldInvalid("email", fields) && styles.invalid
              )}>
              <input
                className={emailProps.disabled ? styles.disabled : ""}
                type="text"
                placeholder={renderFieldPlaceHolder("email")}
                {...emailProps}
              />
            </div>
            {fields.repeatEmail && (
              <div
                className={classnames(
                  "wbkv9-CustomerForm-field",
                  styles.field,
                  isRepeatEmailInvalid(fields) && styles.invalid
                )}>
                <input
                  className={emailProps.disabled && styles.disabled}
                  type="text"
                  placeholder={renderFieldPlaceHolder("repeatEmail")}
                  {...repeatEmailProps}
                  disabled={emailProps.disabled}
                  readOnly={emailProps.readOnly}
                />
              </div>
            )}
            {fields.address && (
              <div
                className={classnames(
                  "wbkv9-CustomerForm-field",
                  styles.field,
                  isFieldInvalid("address", fields) && styles.invalid
                )}>
                <input
                  type="text"
                  placeholder={renderFieldPlaceHolder("address")}
                  {...addressProps}
                />
              </div>
            )}
            {fields.idDocument && (
              <div
                className={classnames(
                  "wbkv9-CustomerForm-field",
                  styles.field,
                  isFieldInvalid("idDocument", fields) && styles.invalid
                )}>
                <input
                  type="text"
                  placeholder={renderFieldPlaceHolder("idDocument")}
                  {...idDocumentProps}
                />
              </div>
            )}

            {fields.arrivalTime && (
              <div
                className={classnames(
                  "wbkv9-CustomerForm-field",
                  styles.field,
                  isFieldInvalid("arrivalTime", fields) && styles.invalid
                )}>
                <select
                  type="text"
                  {...arrivalTimeProps}
                  className={classnames({
                    "wbkv9-CustomerForm-field-country--selected": !!fields.arrivalTime.value,
                    [styles.selected]: !!fields.arrivalTime.value,
                    "wbkv9-CustomerForm-field-country--unselected": !fields.arrivalTime.value,
                    [styles.unselected]: !fields.arrivalTime.value,
                  })}>
                  <option key={-1} value="" disabled hidden>
                    {renderFieldPlaceHolder("arrivalTime")}
                  </option>
                  {hours &&
                    hours.map((hour, idx) => (
                      <option key={idx} value={hour.value}>
                        {hour.text}
                      </option>
                    ))}
                </select>
              </div>
            )}
            {guestFormQuestionnaire &&
              guestFormQuestionnaire.map((form, idx) => {
                return (
                  <StyledFormGenerator
                    key={idx}
                    updateFormValues={updateCustomFormValues}
                    formShape={form}
                    isFormValid={isFormValid}
                    datesList={reservationsDates}
                    touch={touchFormGenerator}
                    inCustomerForm
                  />
                );
              })}

            <div
              className={classnames(
                "wbkv9-CustomerForm-field",
                "wbkv9-CustomerForm-comments",
                styles.comments,
                styles.field
              )}>
              <textarea
                type="text"
                placeholder={renderFieldPlaceHolder("comments")}
                {...commentsProps}
              />
            </div>
            {reservations &&
              reservations.map((reservation, idx) => {
                // First checks if the current reservation has formValues
                let doesCurrentReservationHasFormValues = false;
                if (reservation.get("selectedServices")) {
                  reservation.get("selectedServices").forEach(selectedService => {
                    if (
                      selectedService.get("formValues") ||
                      selectedService.get("dateSelectionType") ===
                        constants.DATE_SELECTION_TYPE.SPECIFIC
                    ) {
                      doesCurrentReservationHasFormValues = true;
                    }
                  });
                }
                // If it does, renders the component
                if (doesCurrentReservationHasFormValues) {
                  return (
                    <ExtrasAdditionalInfo
                      key={idx}
                      accomodationName={reservation
                        .get("inventory")
                        .get("accommodation")
                        .get("name")}
                      selectedServices={reservation.get("selectedServices").toJS()}
                      totalOccupancy={reservation.get("totalOccupancy")}
                    />
                  );
                }
              })}

            {fields.newsletter && (
              <div
                className={classnames(
                  "wbkv9-CustomerForm-field",
                  styles.field,
                  "wbkv9-CustomerForm-promo",
                  styles.promo
                )}>
                <div className={styles.promoField}>
                  <input type="checkbox" {...newsletterProps} checked={fields.newsletter.value} />

                  <span
                    className={classnames(
                      "wbkv9-CustomerForm-promoFieldLink",
                      styles.promoFieldLink
                    )}>
                    {formatMessage(msgs.promoField)}
                  </span>

                  <a
                    className={classnames(
                      "wbkv9-CustomerForm-promoFieldLinkMobile",
                      styles.promoFieldLinkMobile
                    )}
                    onClick={() => communicationsDisclaimer && handlePromosModal()}>
                    {formatMessage(msgs.promoField)}
                  </a>
                </div>

                {communicationsDisclaimer && (
                  <div
                    className={classnames(
                      "wbkv9-CustomerForm-communicationsDisclaimer",
                      styles.communicationsDisclaimer
                    )}>
                    <div
                      className={classnames("wbkv9-CustomerForm-promoText", styles.promoText)}
                      dangerouslySetInnerHTML={{ __html: communicationsDisclaimer }}
                    />

                    <div
                      className={classnames(
                        "wbkv9-CustomerForm-promoTextReadMoreContainer",
                        styles.promoTextReadMoreContainer
                      )}>
                      <div
                        className={classnames(
                          "wbkv9-CustomerForm-promoTextReadMore",
                          styles.promoTextReadMore
                        )}
                        onClick={() => communicationsDisclaimer && handlePromosModal()}>
                        <FormattedMessage {...msgs.readMore} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

CustomerForm.propTypes = {
  fields: PropTypes.object,
  fieldConfiguration: PropTypes.object,
  intl: PropTypes.object,
};

export default injectIntl(pure(CustomerForm));
